import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react"
import { usePinchPan } from "../../../hooks/usePinchPan"

export type VisualiserImageAreaViewModelOptions = {
  /** Image to display; consumer obtains the file via their own upload UI. */
  file?: File | null
  backgroundColor: string
  size?: {
    width: string
    height: string
  }
  position?: {
    top?: string
    bottom?: string
    left?: string
    right?: string
  }
  borderRadius: string
  style?: CSSProperties
  className?: string
  alt: string
  scale: number
  x: number
  y: number
  onUpdate?: (updates: { scale?: number; x?: number; y?: number }) => void
  rootClassNameBase: string
}

const MIN_SCALE = 0.5
const MAX_SCALE = 5

/**
 * Object URL lifecycle, pinch/pan binding, wheel zoom, and layout for the image visualiser.
 */
export function useVisualiserImageAreaViewModel(options: VisualiserImageAreaViewModelOptions) {
  const {
    file,
    backgroundColor,
    size,
    position,
    borderRadius,
    style: extraStyle,
    className,
    alt,
    scale,
    x,
    y,
    onUpdate,
    rootClassNameBase,
  } = options

  const selectedFile = useMemo(() => (file instanceof File ? file : null), [file])

  const [objectUrl, setObjectUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!selectedFile) {
      setObjectUrl(null)
      return
    }
    const url = URL.createObjectURL(selectedFile)
    setObjectUrl(url)
    return () => {
      URL.revokeObjectURL(url)
    }
  }, [selectedFile])

  const src = objectUrl || ""
  const hasImage = Boolean(src)
  const [active, setActive] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const bind = usePinchPan({
    scale,
    x,
    y,
    onChange: (updates) => {
      if (onUpdate) {
        onUpdate(updates)
      }
    },
    minScale: MIN_SCALE,
    maxScale: MAX_SCALE,
  })

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (!hasImage) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      const sensitivity = 0.001
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale * (1 - event.deltaY * sensitivity)))
      if (newScale !== scale && onUpdate) {
        onUpdate({ scale: newScale })
      }
    },
    [hasImage, scale, onUpdate],
  )

  useEffect(() => {
    const container = containerRef.current
    if (!container || !hasImage) {
      return
    }
    container.addEventListener("wheel", handleWheel, { passive: false })
    return () => {
      container.removeEventListener("wheel", handleWheel)
    }
  }, [handleWheel, hasImage])

  const rootClassName = className ? `${rootClassNameBase} ${className}` : rootClassNameBase

  const containerStyle: CSSProperties = {
    position: "absolute",
    top: position?.top,
    bottom: position?.bottom,
    left: position?.left,
    right: position?.right,
    width: size?.width || "90%",
    height: size?.height || "65%",
    backgroundColor,
    borderRadius,
    overflow: "hidden",
    touchAction: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    userSelect: "none",
    WebkitUserSelect: "none",
    cursor: hasImage ? (active ? "grabbing" : "grab") : "default",
    ...extraStyle,
  }

  const imageStyle: CSSProperties = {
    transform: `translate3d(${x}px, ${y}px, 0) scale(${scale})`,
    maxWidth: "none",
    maxHeight: "none",
    pointerEvents: "none",
    willChange: "transform",
  }

  return {
    containerRef,
    spreadHandlers: hasImage ? bind() : {},
    onMouseDown: () => hasImage && setActive(true),
    onMouseUp: () => setActive(false),
    onMouseLeave: () => setActive(false),
    rootClassName,
    containerStyle,
    hasImage,
    src,
    alt,
    imageStyle,
  }
}
